import { useEffect, useMemo, useRef, useState } from 'react';

import CenteredOverlay from './CenteredOverlay';

interface Hotkey {
  keys: string[];
  description: string;
  group: string;
}

const HOTKEYS: Hotkey[] = [
  { keys: ['t'], description: 'Today', group: 'Navigation' },
  { keys: ['→', 'j'], description: 'Next', group: 'Navigation' },
  { keys: ['←', 'k'], description: 'Previous', group: 'Navigation' },
  { keys: ['m'], description: 'Month view (press again to pick month)', group: 'View' },
  { keys: ['w'], description: 'Week view (press again to pick week)', group: 'View' },
  { keys: ['d'], description: 'Day view', group: 'View' },
  { keys: ['r'], description: 'Sync now', group: 'Actions' },
  { keys: ['n'], description: "Open today's daily note", group: 'Actions' },
  { keys: ['Space'], description: 'Quick add event', group: 'Actions' },
  { keys: ['p'], description: 'Plan', group: 'App' },
  { keys: [','], description: 'Settings', group: 'App' },
  { keys: ['?'], description: 'Keyboard shortcuts', group: 'App' },
  { keys: ['Esc'], description: 'Close popup', group: 'App' }
];

interface GrammarItem {
  example: string;
  meaning: string;
  group: string;
}

// Cheatsheet for the QuickAdd parser (backend/rialto/quickadd.py). Phrasings the
// parser actually recognizes — keep in sync when the grammar changes.
const QUICKADD_GRAMMAR: GrammarItem[] = [
  { example: 'today, tomorrow, tonight', meaning: 'Relative day', group: 'Dates' },
  { example: 'next Fri, this Mon, last Tue', meaning: 'Anchored weekday', group: 'Dates' },
  { example: 'in 3 days, 2 weeks from now', meaning: 'Offset from today', group: 'Dates' },
  { example: 'in 2 years, next year', meaning: 'Offset from today', group: 'Dates' },
  { example: 'June 15, 15 June, 6/15', meaning: 'Calendar date', group: 'Dates' },
  { example: "June 15 2027, 15 June '27", meaning: 'Dated year', group: 'Dates' },
  { example: '6/15/2027, 6/15/27, 2027-06-15', meaning: 'Numeric with year', group: 'Dates' },
  { example: 'June 2027', meaning: 'Month, first of', group: 'Dates' },

  { example: 'June 1-3, June 30 to July 2', meaning: 'Multi-day span', group: 'Date ranges' },
  { example: '1-3 July, 6/1-6/5', meaning: 'Multi-day span', group: 'Date ranges' },
  { example: 'June 1-3 2027, 6/1/27-6/5/27', meaning: 'Span with year', group: 'Date ranges' },
  { example: 'Mon-Fri, Monday to Friday', meaning: 'Weekday span', group: 'Date ranges' },

  { example: '3pm, 15:00, noon', meaning: 'Start time', group: 'Times' },
  { example: '3-5pm, 3pm-5, 14:00-16:00', meaning: 'Time range', group: 'Times' },
  { example: 'for 90 min, for 1.5 hours', meaning: 'Duration', group: 'Times' },
  { example: 'all day', meaning: 'All-day event', group: 'Times' },

  { example: 'every Mon, every Mon & Wed', meaning: 'Weekly on weekday(s)', group: 'Recurrence' },
  { example: 'daily, weekly, biweekly, monthly', meaning: 'Frequency', group: 'Recurrence' },
  { example: 'monthly 15th, first Mon of the month', meaning: 'Monthly', group: 'Recurrence' },
  { example: 'until Aug 1, for 5 times', meaning: 'Repeat end', group: 'Recurrence' },

  { example: 'gym, work, default', meaning: 'Pick calendar by keyword', group: 'Calendar & more' },
  { example: '#Room 204#', meaning: 'Location', group: 'Calendar & more' },
  { example: '"Q3 planning"', meaning: 'Literal title, skips parsing', group: 'Calendar & more' },
  { example: 'countdown, countdown to', meaning: 'Sidebar countdown badge', group: 'Calendar & more' }
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function HotkeysHelp({ open, onClose }: Props) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    // Defer focus past the keydown that summoned the modal so the trigger
    // event doesn't land in the input.
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return HOTKEYS;
    return HOTKEYS.filter(
      (h) =>
        h.description.toLowerCase().includes(q) ||
        h.group.toLowerCase().includes(q) ||
        h.keys.some((k) => k.toLowerCase().includes(q))
    );
  }, [query]);

  const grouped = useMemo(() => {
    const map = new Map<string, Hotkey[]>();
    for (const h of filtered) {
      const arr = map.get(h.group) ?? [];
      arr.push(h);
      map.set(h.group, arr);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const groupedGrammar = useMemo(() => {
    const q = query.trim().toLowerCase();
    const items = q
      ? QUICKADD_GRAMMAR.filter(
          (g) =>
            g.example.toLowerCase().includes(q) ||
            g.meaning.toLowerCase().includes(q) ||
            g.group.toLowerCase().includes(q)
        )
      : QUICKADD_GRAMMAR;
    const map = new Map<string, GrammarItem[]>();
    for (const g of items) {
      const arr = map.get(g.group) ?? [];
      arr.push(g);
      map.set(g.group, arr);
    }
    return Array.from(map.entries());
  }, [query]);

  if (!open) return null;

  return (
    <CenteredOverlay
      ariaLabel="Keyboard shortcuts"
      onClose={onClose}
      maxWidth={520}
      maxHeight="80dvh"
    >
      <header className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
          <div className="text-sm font-semibold">Keyboard shortcuts</div>
          <button
            type="button"
            aria-label="close"
            onClick={onClose}
            className="rounded px-2 py-1 text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
          >
            ✕
          </button>
        </header>
        <div className="border-b border-[var(--border)] p-3">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter…"
            className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-3 py-[6px] text-sm outline-none focus:border-[var(--border-strong)]"
          />
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {grouped.length === 0 && groupedGrammar.length === 0 && (
            <div className="text-sm text-[var(--text-faint)]">No matches.</div>
          )}
          {grouped.map(([group, items]) => (
            <div key={group} className="mb-3 last:mb-0">
              <div className="mb-1 text-[0.72rem] uppercase tracking-wide text-[var(--text-faint)]">
                {group}
              </div>
              <div className="flex flex-col gap-1">
                {items.map((h) => (
                  <div
                    key={h.description}
                    className="flex items-center justify-between gap-3"
                  >
                    <div className="text-sm text-[var(--text)]">{h.description}</div>
                    <div className="flex flex-wrap justify-end gap-1">
                      {h.keys.map((k, i) => (
                        <Kbd key={i}>{k}</Kbd>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {groupedGrammar.length > 0 && (
            <div className="mt-4 border-t border-[var(--border)] pt-3">
              <div className="mb-2 text-[0.72rem] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                Quick add syntax
              </div>
              {groupedGrammar.map(([group, items]) => (
                <div key={group} className="mb-3 last:mb-0">
                  <div className="mb-1 text-[0.72rem] uppercase tracking-wide text-[var(--text-faint)]">
                    {group}
                  </div>
                  <div className="flex flex-col gap-1">
                    {items.map((g) => (
                      <div
                        key={g.example}
                        className="flex items-baseline justify-between gap-3"
                      >
                        <code className="font-mono text-[0.8rem] text-[var(--text)]">
                          {g.example}
                        </code>
                        <div className="text-right text-[0.78rem] text-[var(--text-muted)]">
                          {g.meaning}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
    </CenteredOverlay>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="min-w-[24px] rounded border border-[var(--border-strong)] bg-[var(--bg)] px-[6px] py-[1px] text-center font-mono text-[0.72rem] text-[var(--text-muted)] shadow-[var(--shadow-sm)]">
      {children}
    </kbd>
  );
}
